import { Decoration, ViewPlugin, WidgetType } from "@codemirror/view"
import { store } from "./store"
import { fuzzyFilter } from "./fuzzy"

const EMBED_LINE = /^!\[\[([^\]\n]+)\]\]\s*$/

class EmbedWidget extends WidgetType {
  constructor(entity) {
    super()
    this.entity = entity
  }

  eq(other) {
    return (
      other.entity.id === this.entity.id &&
      other.entity.name === this.entity.name &&
      other.entity.kind === this.entity.kind &&
      other.entity.excerpt === this.entity.excerpt
    )
  }

  toDOM() {
    const card = document.createElement("div")
    card.className = `cm-embed cm-embed-${this.entity.kind}`
    card.dataset.entityId = String(this.entity.id)

    const header = document.createElement("div")
    header.className = "cm-embed-header"

    const sigil = document.createElement("span")
    sigil.className = "cm-embed-sigil"
    sigil.textContent = this.entity.sigil
    header.appendChild(sigil)

    const name = document.createElement("span")
    name.className = "cm-embed-name"
    name.textContent = this.entity.name
    header.appendChild(name)

    const kind = document.createElement("span")
    kind.className = "cm-embed-kind"
    kind.textContent = `— ${this.entity.kind}`
    header.appendChild(kind)

    const hint = document.createElement("span")
    hint.className = "cm-embed-hint"
    hint.textContent = "↵"
    header.appendChild(hint)

    card.appendChild(header)

    const excerpt = document.createElement("p")
    excerpt.className = "cm-embed-excerpt"
    excerpt.textContent = this.entity.excerpt || "Nothing written here yet."
    card.appendChild(excerpt)

    return card
  }

  ignoreEvent(event) {
    return event.type !== "mousedown"
  }
}

function buildEmbedDecorations(view) {
  const decorations = []
  const { state } = view
  const selectionLines = new Set()
  for (const range of state.selection.ranges) {
    const fromLine = state.doc.lineAt(range.from).number
    const toLine = state.doc.lineAt(range.to).number
    for (let n = fromLine; n <= toLine; n++) selectionLines.add(n)
  }

  for (const { from, to } of view.visibleRanges) {
    let pos = from
    while (pos <= to) {
      const line = state.doc.lineAt(pos)
      const match = line.text.match(EMBED_LINE)
      if (match && !selectionLines.has(line.number) && line.length > 0) {
        const entity = store.resolveAny(match[1])
        if (entity) {
          decorations.push(
            Decoration.replace({ widget: new EmbedWidget(entity) }).range(line.from, line.to)
          )
        }
      }
      pos = line.to + 1
    }
  }
  return Decoration.set(decorations, true)
}

export const embedCards = ViewPlugin.fromClass(
  class {
    constructor(view) {
      this.decorations = buildEmbedDecorations(view)
    }

    update(update) {
      if (update.docChanged || update.viewportChanged || update.selectionSet) {
        this.decorations = buildEmbedDecorations(update.view)
      }
    }

    refresh(view) {
      this.decorations = buildEmbedDecorations(view)
    }
  },
  {
    decorations: (plugin) => plugin.decorations,
    eventHandlers: {
      mousedown(event, view) {
        const card = event.target.closest(".cm-embed")
        if (!card) return false
        const entity = store.findEntity(card.dataset.entityId)
        if (!entity) return false
        view.dom.dispatchEvent(
          new CustomEvent("ootline:open-entity", {
            bubbles: true,
            detail: { url: entity.url, id: entity.id }
          })
        )
        return true
      }
    }
  }
)

export function openEmbedAtCursor(view) {
  const line = view.state.doc.lineAt(view.state.selection.main.head)
  const match = line.text.match(EMBED_LINE)
  if (!match) return false
  const entity = store.resolveAny(match[1])
  if (!entity) return false
  view.dom.dispatchEvent(
    new CustomEvent("ootline:open-entity", { bubbles: true, detail: { url: entity.url, id: entity.id } })
  )
  return true
}

export function embedCompletions(context) {
  const match = context.matchBefore(/!\[\[([^\]\n]*)$/)
  if (!match) return null

  const query = match.text.slice(3)
  const matches = fuzzyFilter(query, store.entities, (entity) => entity.name)
  if (!matches.length) return null

  return {
    from: match.from,
    filter: false,
    options: matches.map((entity) => ({
      label: entity.name,
      detail: `embed ${entity.kind}`,
      apply: `![[${entity.name}]]`
    }))
  }
}
